import { useRef } from "react";
import PropTypes from "prop-types";
import {
  Content,
  Text,
  Card,
  CardPreview,
  AvatarGroup,
  Badge,
  ActionButton,
  DialogTrigger,
} from "@react-spectrum/s2";
import { useStore } from "zustand";
import { useHover } from "@react-aria/interactions";

import { store } from "../../store";

import { useStory } from "@app/hooks/stories";

import { PersonaAvatar } from "@app/components/PersonaAvatar";

import { StoryEditDialog } from "./StoryEditDialog";

import MaximizeIcon from "@react-spectrum/s2/icons/Maximize";

import { StoryImage } from "./StoryImage";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const StoryCard = ({
  item = {},
  storyId,
  variant = "quiet",
  styles,
}) => {
  const { hoverProps, isHovered } = useHover({});

  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const colorScheme = useStore(store, (state) => state.colorScheme);
  const isDark = colorScheme === "dark";

  const cardPreviewRef = useRef(null);

  return (
    <>
      <Card styles={styles} variant={variant}>
        <CardPreview>
          <div ref={cardPreviewRef} {...hoverProps}>
            <StoryImage visuals={item?.visuals} />
            {/* <ImageIllustration /> */}
            <Badge
              variant="neutral"
              fillStyle="subtle"
              size="S"
              styles={style({
                position: "absolute",
                bottom: 8,
                insetStart: 8,
              })}
            >
              {item.index ?? item.id + 1}
            </Badge>
            <AvatarGroup
              size={24}
              styles={style({
                position: "absolute",
                bottom: 8,
                insetEnd: 8,
              })}
              aria-label="Personas"
            >
              {item?.personas?.map?.((personaIdx, idx) => (
                <PersonaAvatar
                  key={`${personaIdx}-${idx}`}
                  persona={story?.personas?.[personaIdx]}
                  isOverBackground
                />
              ))}
            </AvatarGroup>
            <div
              className={style({
                position: "absolute",
                top: "50%",
                insetEnd: "50%",
                translateX: "50%",
                translateY: "-50%",
                opacity: {
                  default: 0,
                  isHovered: 1,
                },
              })({ isHovered })}
            >
              <DialogTrigger>
                <ActionButton
                  variant="primary"
                  staticColor={isDark ? "black" : "white"}
                  aria-label="Maximize card"
                >
                  <MaximizeIcon />
                </ActionButton>
                <StoryEditDialog key={item.id} storyId={storyId} item={item} />
              </DialogTrigger>
            </div>
          </div>
        </CardPreview>
        <Content styles={style({ alignContent: "start" })}>
          <div className={style({ font: "detail-sm" })}>
            <Text>{item.section}</Text>
          </div>
          <div>
            <Text slot="title">{item.title}</Text>
            <div
              className={style({
                font: "detail",
                whiteSpace: "pre-line",
              })}
            >
              <Text>{item.text}</Text>
            </div>
          </div>
        </Content>
        {/* <Footer>
          <div
            className={style({
              display: "flex",
              gap: 8,
              flexDirection: "column",
            })}
          >
            {!!item?.solutions?.length && (
              <TagGroup selectionMode="none" size="S" aria-label="Solutions">
                {item?.solutions?.map?.((solution, idx) => (
                  <Tag key={`${solution}-${idx}`}>{solution}</Tag>
                ))}
              </TagGroup>
            )}
          </div>
        </Footer> */}
      </Card>
    </>
  );
};

StoryCard.propTypes = {
  item: PropTypes.object,
  storyId: PropTypes.string,
  variant: PropTypes.string,
  styles: PropTypes.object,
};
